import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import React, { useMemo, useState } from 'react';
import { CameraDetails } from '../../../types';
import { formatDate, getProcessingTypeColor } from '../../../utils';

interface ModListProps {
  cameras: CameraDetails[];
  selectedCamera: CameraDetails | null;
  onCameraSelect: (camera: CameraDetails) => void;
}

const ModList: React.FC<ModListProps> = ({ cameras, selectedCamera, onCameraSelect }) => {
  const [search, setSearch] = useState('');

  const filteredCameras = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    if (!search) {
      return cameras;
    }
    return cameras.filter(
      (camera) =>
        camera.deviceName.toLowerCase().includes(lowerSearch) ||
        (lowerSearch === 'compliance' && camera.mods.some((mod) => mod.isComplianceEnabled)) ||
        camera.mods.some(
          (mod) =>
            mod.tagName.toLowerCase().includes(lowerSearch) ||
            mod.processingType.toLowerCase().includes(lowerSearch)
        )
    );
  }, [cameras, search]);

  const metrics = useMemo(() => {
    const devices: Record<string, number> = {};
    const tags: Record<string, number> = {};
    const processingTypes: Record<string, number> = {};
    let complianceCameras = 0;
    let complianceMods = 0;
    filteredCameras.forEach((camera) => {
      devices[camera.deviceName] = (devices[camera.deviceName] || 0) + 1;
      const cameraProcessingTypes = new Set<string>();
      let cameraHasCompliance = false;
      camera.mods.forEach((mod) => {
        tags[mod.tagName] = (tags[mod.tagName] || 0) + 1;
        cameraProcessingTypes.add(mod.processingType);
        if (mod.isComplianceEnabled) {
          complianceMods++;
          cameraHasCompliance = true;
        }
      });
      [...cameraProcessingTypes].forEach((type) => {
        processingTypes[type] = (processingTypes[type] || 0) + 1;
      });
      if (cameraHasCompliance) {
        complianceCameras++;
      }
    });
    return {
      devices: Object.keys(devices).length,
      tags: Object.keys(tags).length,
      processingTypes,
      complianceCameras,
      complianceMods,
    };
  }, [filteredCameras]);

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-4 border-b">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Search tags, devices, processing types..."
          />
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="p-4 border-b">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-gray-100 rounded-md p-3 flex flex-col items-center justify-center">
            <div className="text-gray-600">Devices</div>
            <div className="font-medium text-lg">{metrics.devices}</div>
          </div>
          <div className="bg-gray-100 rounded-md p-3 flex flex-col items-center justify-center">
            <div className="text-gray-600">Tags</div>
            <div className="font-medium text-lg">{metrics.tags}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-col items-center text-sm">
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(metrics.processingTypes).map(([type, count]) => (
              <span
                key={type}
                className={`px-2 py-1 rounded-full text-xs ${getProcessingTypeColor(type)}`}
                onClick={() => setSearch(type)}
                style={{ cursor: 'pointer' }}
              >
                {type}: <span className="font-medium">{count}</span>
              </span>
            ))}
            {metrics.complianceCameras > 0 && (
              <span
                className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800"
                onClick={() => setSearch('compliance')}
                style={{ cursor: 'pointer' }}
              >
                Compliance:{' '}
                <span className="font-medium">
                  {metrics.complianceCameras} ({metrics.complianceMods})
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        {filteredCameras.map((camera) => (
          <div
            key={camera.cameraId}
            onClick={() => onCameraSelect(camera)}
            className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
              selectedCamera?.cameraId === camera.cameraId ? 'bg-blue-50 border-blue-200' : ''
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium">
                {camera.mods.map((mod, idx) => (
                  <React.Fragment key={mod.tagName}>
                    {idx > 0 && ', '}
                    <span className={mod.isComplianceEnabled ? 'text-amber-700' : ''}>
                      {mod.tagName}
                    </span>
                  </React.Fragment>
                ))}
              </h3>
              <div className="flex flex-col items-end gap-1">
                {[...new Set(camera.mods.map((mod) => mod.processingType))].map(
                  (processingType) => (
                    <span
                      key={processingType}
                      className={`px-2 py-1 rounded-full text-xs ${getProcessingTypeColor(
                        processingType
                      )}`}
                    >
                      {processingType}
                    </span>
                  )
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">{camera.deviceName}</p>
            <p className="text-xs text-gray-500">{formatDate(camera.imageCreatedAt)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ModList;
